import DOMPurify from 'dompurify';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { aiApi, isAiGenerationCancelled } from '@/api/ai';
import { SECTIONS } from '@/components/wizard/constants';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { toReportStepKey } from '@/lib/analytics-events';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { capture, captureFeatureBlocked, toFailureKind } from '@/lib/posthog';
import { isBuiltInReportSectionId } from '@/lib/report-sections';
import { AI_PRESETS, PANEL_CLOSE_ANIMATION_MS } from './constants';
import { hasMeaningfulHtmlContent } from './helpers';
import type { AIGenerationEntry, AIStatus } from './types';
import type { AIAdditionalData, AIImprovementIntent } from '@/api/ai';
import type { Editor } from '@tiptap/react';

type UseAIAssistantParams = {
    editor: Editor | null;
    onChange: (value: string) => void;
    reportId?: string;
    sectionId?: string;
    onBeforeGenerate?: () => Promise<void>;
    onAIPanelOpenChange?: (open: boolean) => void;
    /**
     * Preset to open the panel with and fire immediately, once, as soon as the
     * editor exists. Set when the user picked an AI action from the section's
     * right-click menu, where there is no editor yet to run it against.
     */
    autoRunIntent?: AIImprovementIntent | null;
};

export const useAIAssistant = ({
    editor,
    onChange,
    reportId,
    sectionId,
    onBeforeGenerate,
    onAIPanelOpenChange,
    autoRunIntent,
}: UseAIAssistantParams) => {
    const [isAIPanelOpen, setIsAIPanelOpen] = useState(false);
    const [isAIPanelRendered, setIsAIPanelRendered] = useState(false);
    const [isAIPanelVisible, setIsAIPanelVisible] = useState(false);
    const [aiStatus, setAiStatus] = useState<AIStatus>('idle');
    const [aiPrompt, setAiPrompt] = useState<AIImprovementIntent | ''>('');
    const [aiError, setAiError] = useState<string | null>(null);

    const [generations, setGenerations] = useState<AIGenerationEntry[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const aiCreditAccess = useFeatureEntitlement('ai.credits');

    const generationIdRef = useRef(0);
    const abortControllerRef = useRef<AbortController | null>(null);
    // The improve path answers on the request itself, but a section that fell
    // back to a job hands one back mid-flight — and that id is the only way to
    // stop the work rather than just stop listening to it.
    const jobIdRef = useRef<string | null>(null);
    const closePanelTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const hasAIContext = Boolean(reportId && sectionId);

    // Derive current content from the generation history
    const currentEntry = generations[currentIndex] ?? null;
    const streamedText = currentEntry?.content ?? '';
    const sanitizedStreamedText = useMemo(
        () => (currentEntry ? currentEntry.sanitizedContent : ''),
        [currentEntry],
    );
    const currentPresetLabel = currentEntry?.presetLabel ?? null;

    const resolvePreset = useCallback(
        (intent: AIImprovementIntent | '') => AI_PRESETS.find((preset) => preset.key === intent),
        [],
    );

    const handleGenerateAI = useCallback(
        async (intentOverride?: AIImprovementIntent) => {
            if (!editor || !reportId || !sectionId) return;

            if (aiCreditAccess.shouldBlock) {
                captureFeatureBlocked('report_editor', aiCreditAccess.decision);
                const message = getEntitlementBlockedMessage(aiCreditAccess.decision);
                setAiStatus('prompt');
                setAiError(message);
                toast.warning(message);
                return;
            }

            await onBeforeGenerate?.();

            abortControllerRef.current?.abort();
            const abortController = new AbortController();
            abortControllerRef.current = abortController;
            jobIdRef.current = null;

            const generationId = generationIdRef.current + 1;
            generationIdRef.current = generationId;

            setAiStatus('streaming');
            setAiError(null);

            const currentHTML = editor.getHTML();
            const hasContent = hasMeaningfulHtmlContent(currentHTML);
            const mode = hasContent ? 'improve' : 'create';
            const selectedIntent = intentOverride ?? aiPrompt;
            const preset = resolvePreset(selectedIntent);

            if (!preset) {
                setAiStatus('prompt');
                setAiError('Selecione uma ação de IA antes de continuar.');
                return;
            }

            const additionalData: AIAdditionalData | undefined =
                mode === 'improve' && preset ? { improvement_intent: preset.key } : undefined;

            // The section id is a plain string here, so custom sections have to
            // be recognised before `toReportStepKey` can bucket them — passing
            // one raw would ship a per-report id (R3).
            const stepKey = toReportStepKey(
                sectionId,
                !isBuiltInReportSectionId(sectionId) && sectionId !== SECTIONS.PATIENT,
            );
            if (stepKey) {
                capture('report_ai_generation_requested', {
                    step_key: stepKey,
                    surface: 'inline_editor',
                    mode,
                });
            }

            try {
                const response = await aiApi.generate(
                    reportId,
                    sectionId,
                    additionalData,
                    mode,
                    hasContent ? currentHTML : undefined,
                    {
                        signal: abortController.signal,
                        onJobStarted: (jobId) => {
                            jobIdRef.current = jobId;
                        },
                    },
                );

                if (generationIdRef.current === generationId) {
                    const entry: AIGenerationEntry = {
                        content: response.content,
                        sanitizedContent: DOMPurify.sanitize(response.content),
                        presetKey: preset.key,
                        presetLabel: preset.label,
                    };

                    setGenerations((prev) => {
                        const next = [...prev, entry];
                        setCurrentIndex(next.length - 1);
                        return next;
                    });
                    setAiStatus('review');
                }
            } catch (error) {
                if (abortController.signal.aborted || isAiGenerationCancelled(error)) return;

                console.error('Error generating AI content:', error);
                capture('report_action_failed', {
                    action: 'ai_generate',
                    failure_kind: toFailureKind(error),
                });

                if (generationIdRef.current === generationId) {
                    const message =
                        error instanceof Error && error.message.trim()
                            ? error.message
                            : 'Erro ao gerar conteúdo. Tente novamente.';
                    setAiError(message);
                    setAiStatus('prompt');
                    toast.error(message);
                }
            }
        },
        [
            aiCreditAccess.decision,
            aiCreditAccess.shouldBlock,
            aiPrompt,
            editor,
            onBeforeGenerate,
            reportId,
            resolvePreset,
            sectionId,
        ],
    );

    /**
     * Stops the generation on both sides.
     *
     * Aborting the request alone only stops this panel from listening: the
     * writer keeps running and the credit is still spent. Cancelling the job
     * first is what makes the button mean what it says.
     */
    const stopGeneration = useCallback(() => {
        const jobId = jobIdRef.current;
        if (jobId) {
            void aiApi.cancelJob(jobId).catch(() => {
                // Stopped for the user either way; a failed cancel only leaves
                // the worker finishing a draft nobody will be served.
            });
        }
        jobIdRef.current = null;
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        generationIdRef.current += 1;
    }, []);

    const handleCancelGeneration = useCallback(() => {
        stopGeneration();
        setAiStatus('prompt');
    }, [stopGeneration]);

    const handleOpenAIPanel = useCallback(() => {
        if (closePanelTimeoutRef.current) {
            clearTimeout(closePanelTimeoutRef.current);
            closePanelTimeoutRef.current = null;
        }

        onAIPanelOpenChange?.(true);
        setIsAIPanelRendered(true);
        setIsAIPanelVisible(false);
        setIsAIPanelOpen(true);
        setAiStatus('prompt');
        setAiPrompt('');
        setAiError(null);
        setGenerations([]);
        setCurrentIndex(0);
    }, [onAIPanelOpenChange]);

    const handleCloseAIPanel = useCallback(() => {
        stopGeneration();
        onAIPanelOpenChange?.(false);
        setIsAIPanelOpen(false);
        setIsAIPanelVisible(false);
        setAiStatus('idle');
        setAiError(null);
        setGenerations([]);
        setCurrentIndex(0);

        if (closePanelTimeoutRef.current) {
            clearTimeout(closePanelTimeoutRef.current);
        }

        closePanelTimeoutRef.current = setTimeout(() => {
            setIsAIPanelRendered(false);
            closePanelTimeoutRef.current = null;
        }, PANEL_CLOSE_ANIMATION_MS);
    }, [onAIPanelOpenChange, stopGeneration]);

    const handleApplyAI = useCallback(() => {
        if (!editor || !streamedText) return;

        const previousContent = editor.getHTML();

        editor.commands.setContent(streamedText);
        onChange(streamedText);
        handleCloseAIPanel();

        toast.success('Conteúdo aplicado', {
            action: {
                label: 'Desfazer',
                onClick: () => {
                    editor.commands.setContent(previousContent);
                    onChange(previousContent);
                },
            },
        });
    }, [editor, handleCloseAIPanel, onChange, streamedText]);

    const handleRegenerate = useCallback(() => {
        // Go back to prompt phase with the current entry's preset pre-selected
        const entry = generations[currentIndex];
        if (entry) {
            const match = AI_PRESETS.find((p) => p.key === entry.presetKey);
            setAiPrompt(match?.key ?? '');
        }
        setAiStatus('prompt');
        setAiError(null);
    }, [currentIndex, generations]);

    const handleNavigateHistory = useCallback(
        (direction: 'prev' | 'next') => {
            setCurrentIndex((prev) => {
                if (direction === 'prev') return Math.max(0, prev - 1);
                return Math.min(generations.length - 1, prev + 1);
            });
        },
        [generations.length],
    );

    const handlePresetSelect = useCallback((intent: AIImprovementIntent) => {
        setAiPrompt(intent);
    }, []);

    // Fire the preset chosen in the section's context menu as soon as the editor
    // it opened is alive. `handleOpenAIPanel` clears `aiPrompt`, so the intent
    // has to ride the override argument rather than the prompt state. The ref
    // makes this one-shot: the panel is the user's from here on, and a rerun
    // would wipe a generation they were reviewing.
    const autoRunConsumedRef = useRef(false);

    useEffect(() => {
        if (!autoRunIntent || !editor || autoRunConsumedRef.current) return;

        autoRunConsumedRef.current = true;
        handleOpenAIPanel();
        void handleGenerateAI(autoRunIntent);
    }, [autoRunIntent, editor, handleGenerateAI, handleOpenAIPanel]);

    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
            if (closePanelTimeoutRef.current) {
                clearTimeout(closePanelTimeoutRef.current);
            }
            onAIPanelOpenChange?.(false);
        };
    }, [onAIPanelOpenChange]);

    return {
        hasAIContext,
        aiPrompt,
        setAiPrompt,
        aiStatus,
        aiError,
        streamedText,
        sanitizedStreamedText,
        generations,
        currentIndex,
        currentPresetLabel,
        isAIPanelOpen,
        isAIPanelRendered,
        isAIPanelVisible,
        setIsAIPanelVisible,
        handleGenerateAI,
        handleCancelGeneration,
        handleOpenAIPanel,
        handleCloseAIPanel,
        handleApplyAI,
        handleRegenerate,
        handleNavigateHistory,
        handlePresetSelect,
        aiCreditAccess,
    };
};
